import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Mountain, Map, User } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { ROUTES } from "@/lib/routes";

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const { refreshSubscription, tier } = useSubscription();

  useEffect(() => {
    // Refresh subscription status on mount
    refreshSubscription();
    
    // Poll a few times to ensure status is updated
    const interval = setInterval(() => {
      refreshSubscription();
    }, 2000);

    return () => clearInterval(interval);
  }, [refreshSubscription]);

  const tierDisplay = tier === 'adventurer' ? 'Adventurer' : tier === 'organizer' ? 'Organizer' : 'Scout';

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg border-primary/20 shadow-xl">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-primary" />
          </div>
          <CardTitle className="text-3xl font-bold">Welcome to {tierDisplay}!</CardTitle>
          <CardDescription className="text-lg">
            Your subscription is now active. Time to explore!
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground mb-2">You now have access to:</p>
            <ul className="text-sm space-y-1">
              <li className="flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                Community run details, photos & waypoints
              </li>
              <li className="flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                Trail difficulty feedback & sentiment
              </li>
              <li className="flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                Run privacy controls
              </li>
              <li className="flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                Premium dashboard insights
              </li>
            </ul>
          </div>

          <div className="grid gap-3">
            <Button 
              variant="hero" 
              size="lg" 
              className="w-full"
              onClick={() => navigate(ROUTES.dashboard)}
            >
              <Mountain className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
            
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate(ROUTES.explore)}
              >
                <Map className="w-4 h-4 mr-2" />
                Explore Trails
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate(ROUTES.settings)}
              >
                <User className="w-4 h-4 mr-2" />
                View Profile
              </Button>
            </div>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Questions? Visit our <a href="/contact" className="text-primary hover:underline">contact page</a> for support.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckoutSuccess;
